/**
 * Type rules + assignment helpers — Track R.
 * Nested type_rules keys stay snake_case (shallow keysToCamel).
 *
 * Orthogonal knobs (do not fuse):
 *   T = typeCount  — how many node families
 *   R = role count — still 2 for classic_fe (friend / enemy)
 *
 * Families change *who* edges may point at, not the soft field F and not R.
 */

import type { SimParams } from "./params";
import type { Rng } from "./rng";

export type TypeRulesNormalized = {
  /** role id → T×T matrix, entries 0 | 1 */
  allow: Record<string, number[][]>;
};

/** Canonical multi-family preset ids (and legacy alias). */
export const TRIBES_FE_PRESETS = new Set(["tribes_fe", "two_tribes_fe"]);

/**
 * In-group friends / out-group enemies for any T ≥ 1.
 * friend[i][j] = 1 iff i===j; enemy[i][j] = 1 iff i!==j.
 * T=2 recovers the old hard-coded two_tribes_fe matrices.
 */
export function tribesFeAllow(T: number): {
  friend: number[][];
  enemy: number[][];
} {
  const t = Math.max(1, T | 0);
  const friend: number[][] = [];
  const enemy: number[][] = [];
  for (let i = 0; i < t; i++) {
    const fRow = new Array<number>(t);
    const eRow = new Array<number>(t);
    for (let j = 0; j < t; j++) {
      fRow[j] = i === j ? 1 : 0;
      eRow[j] = i === j ? 0 : 1;
    }
    friend.push(fRow);
    enemy.push(eRow);
  }
  return { friend, enemy };
}

function is01Matrix(m: unknown, T: number): m is number[][] {
  if (!Array.isArray(m) || m.length !== T) return false;
  for (const row of m) {
    if (!Array.isArray(row) || row.length !== T) return false;
    for (const v of row) {
      if (v !== 0 && v !== 1) return false;
    }
  }
  return true;
}

/**
 * Expand/validate type_rules from params.
 * Throws on unknown preset or bad shape.
 */
export function expandTypeRules(params: SimParams): TypeRulesNormalized {
  const T = params.typeCount;
  const raw = params.typeRules as Record<string, unknown> | null | undefined;

  if ((!raw || raw === null) && T === 1) {
    // Unconstrained classic: allow all for friend/enemy
    const all = Array.from({ length: T }, () =>
      Array.from({ length: T }, () => 1),
    );
    return {
      allow: {
        friend: all.map((r) => r.slice()),
        enemy: all.map((r) => r.slice()),
      },
    };
  }

  const allow: Record<string, number[][]> = {};

  if (raw && typeof raw === "object") {
    const preset = raw.preset;
    if (preset != null && preset !== "none") {
      if (typeof preset === "string" && TRIBES_FE_PRESETS.has(preset)) {
        if (T < 1) {
          throw new Error(
            `type_rules preset ${preset} requires type_count >= 1, got ${T}`,
          );
        }
        const mats = tribesFeAllow(T);
        allow.friend = mats.friend.map((r) => r.slice());
        allow.enemy = mats.enemy.map((r) => r.slice());
      } else {
        throw new Error(`unknown type_rules preset: ${String(preset)}`);
      }
    }

    const allowRaw = raw.allow;
    if (allowRaw && typeof allowRaw === "object") {
      for (const [roleId, mat] of Object.entries(
        allowRaw as Record<string, unknown>,
      )) {
        if (!is01Matrix(mat, T)) {
          throw new Error(
            `type_rules.allow.${roleId} must be ${T}x${T} with entries 0|1`,
          );
        }
        allow[roleId] = mat.map((row) => row.slice());
      }
    }
  }

  if (T > 1 && Object.keys(allow).length === 0) {
    throw new Error(
      "type_count > 1 requires type_rules with preset or allow matrices",
    );
  }

  // Ensure friend/enemy keys exist for T>1 demos
  if (T > 1) {
    if (!allow.friend || !allow.enemy) {
      const zeros = Array.from({ length: T }, () =>
        Array.from({ length: T }, () => 0),
      );
      if (!allow.friend) allow.friend = zeros.map((r) => r.slice());
      if (!allow.enemy) allow.enemy = zeros.map((r) => r.slice());
    }
  }

  return { allow };
}

/**
 * Keep typeCount / type_rules / tribes topology coherent after a UI edit.
 * - T=1 → drop multi-type rules; leave tribes topologies (caller may still reset).
 * - T≥2 without rules → inject tribes_fe so expand never fails mid-slider.
 * - Legacy two_tribes_fe kept as an alias (works for any T).
 */
export function coerceTypeFamilyParams(
  p: SimParams,
  typeCount: number,
): SimParams {
  const T = Math.max(1, Math.min(8, Math.round(Number(typeCount))));
  let typeRules = p.typeRules;
  let topology = p.topology;

  if (T === 1) {
    typeRules = null;
    if (topology === "tribes_blocks" || topology === "tribes_random") {
      topology = "random";
    }
  } else {
    const preset =
      typeRules && typeof typeRules === "object" ? typeRules.preset : undefined;
    if (!typeRules || !preset || preset === "none") {
      typeRules = { preset: "tribes_fe" };
    } else if (typeof preset === "string" && TRIBES_FE_PRESETS.has(preset)) {
      // Keep chosen alias; matrices expand for any T
      typeRules = { ...typeRules, preset };
    }
    // Explicit allow matrices: leave as-is; expand will validate size and throw
    // if they don't match T — UI path should prefer presets.
  }

  return { ...p, typeCount: T, typeRules, topology };
}

/** Fixed counts: type t gets base+1 for t < rem, else base. */
export function typeCounts(n: number, T: number): number[] {
  const base = Math.floor(n / T);
  const rem = n % T;
  const counts = new Array<number>(T);
  for (let t = 0; t < T; t++) {
    counts[t] = base + (t < rem ? 1 : 0);
  }
  return counts;
}

/**
 * tribes_blocks: contiguous index-order blocks, no RNG.
 */
export function assignTypesBlocks(n: number, T: number): Uint8Array {
  const types = new Uint8Array(n);
  const counts = typeCounts(n, T);
  let i = 0;
  for (let t = 0; t < T; t++) {
    const c = counts[t]!;
    for (let k = 0; k < c; k++) {
      types[i++] = t;
    }
  }
  return types;
}

/**
 * tribes_random: fixed counts multiset [0×c0, 1×c1, …] then Fisher–Yates high→low.
 * rng.int(maxExclusive) must match simulation RNG.
 */
export function assignTypesRandom(n: number, T: number, rng: Rng): Uint8Array {
  const types = new Uint8Array(n);
  const counts = typeCounts(n, T);
  let i = 0;
  for (let t = 0; t < T; t++) {
    const c = counts[t]!;
    for (let k = 0; k < c; k++) {
      types[i++] = t;
    }
  }
  // Fisher–Yates: for i from n-1 down to 1, swap i with j in [0, i]
  for (let idx = n - 1; idx >= 1; idx--) {
    const j = rng.int(idx + 1);
    const tmp = types[idx]!;
    types[idx] = types[j]!;
    types[j] = tmp;
  }
  return types;
}

/** Fill eligible indices for agent i and roleId into out (cleared). */
export function eligibleIndices(
  i: number,
  roleId: string,
  types: Uint8Array,
  rules: TypeRulesNormalized,
  n: number,
  forbidSelf: boolean,
  out: number[],
): void {
  out.length = 0;
  const mat = rules.allow[roleId];
  if (!mat) return;
  const ti = types[i]!;
  const row = mat[ti];
  if (!row) return;
  for (let j = 0; j < n; j++) {
    if (forbidSelf && j === i) continue;
    const tj = types[j]!;
    if (row[tj] === 1) out.push(j);
  }
}

export function sampleEligible(
  rng: Rng,
  eligible: number[],
  n: number,
  selfIdx: number,
  forbidSelf: boolean,
): number {
  if (eligible.length > 0) {
    return eligible[rng.int(eligible.length)]!;
  }
  // Global fallback
  if (!forbidSelf || n === 1) return rng.int(n);
  let j = rng.int(n - 1);
  if (j >= selfIdx) j += 1;
  return j;
}

/** Validate tribes preconditions for multi-family demos. */
export function assertTribesViable(n: number, T: number): void {
  if (T < 2) throw new Error("tribes topology requires type_count >= 2");
  if (n < T)
    throw new Error(`tribes topology requires n >= type_count (${n} < ${T})`);
  const counts = typeCounts(n, T);
  for (let t = 0; t < T; t++) {
    if (counts[t]! < 1) throw new Error(`tribes: type ${t} would be empty`);
  }
}
